import { Button, Loader } from "@trafficsharkdsp/ui";
import { lazy, Suspense } from "react";
import { createBrowserRouter, Navigate, Link as RouterLink } from "react-router-dom";

import { AuthenticatedLayout } from "../layouts/AuthenticatedLayout/AuthenticatedLayout";
import { AuthLayout } from "../layouts/AuthLayout/AuthLayout";

import SessionProvider from "./SessionProvider";

import { App } from "@/app/App";
import errorStyles from "@/app/styles/error.module.css";
import { ROUTES } from "@/shared/config";
import { getAccessToken } from "@/shared/config/orval/makeRequest";
import { getRefreshToken } from "@/shared/lib/getRefreshToken";

const RootRedirector = () => {
    const hasSession = Boolean(getAccessToken() || getRefreshToken());

    return <Navigate to={hasSession ? ROUTES.CAMPAIGNS : ROUTES.LOGIN} replace />;
};

const NotFoundPage = () => (
    <div
        style={{
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            textAlign: "center",
            paddingTop: "2rem",
        }}
    >
        <h1>Oops! Page not found</h1>
        <RouterLink to="/">Back to home</RouterLink>
    </div>
);

const GlobalErrorPage = () => (
    <div className={errorStyles.body} style={{ height: "100vh" }}>
        <div className={errorStyles.wrapper}>
            <h2>Oh no!</h2>
            <h3>Something went wrong</h3>
            <p>Don't worry, we will fix it!</p>
            <a href="/">
                <Button>Go home!</Button>
            </a>
        </div>
    </div>
);

const LoginPage = lazy(() => import("@/fsd-pages/login"));
const RegistrationPage = lazy(() => import("@/fsd-pages/registration"));
const ForgotPasswordPage = lazy(() => import("@/fsd-pages/forgot-password"));
const ResetPasswordPage = lazy(() => import("@/fsd-pages/reset-password"));

const AddAppPage = lazy(() => import("@/fsd-pages/create-app"));
const AssetsPage = lazy(() => import("@/fsd-pages/assets"));
const AuditoriesPage = lazy(() => import("@/fsd-pages/auditories"));
const CampaignsPage = lazy(() => import("@/fsd-pages/campaigns"));
const CreateCampaignPage = lazy(() => import("@/fsd-pages/create-campaign"));
const CreativesPage = lazy(() => import("@/fsd-pages/creatives"));
const AddCreativePage = lazy(() => import("@/fsd-pages/creatives-add"));
const FinancesPage = lazy(() => import("@/fsd-pages/finances"));
const PaymentSuccessPage = lazy(() => import("@/fsd-pages/payment-success"));
const PaymentFailedPage = lazy(() => import("@/fsd-pages/payment-failed"));
const ProfilePage = lazy(() => import("@/fsd-pages/profile"));
const ReportsPage = lazy(() => import("@/fsd-pages/reports"));
const TargetsPage = lazy(() => import("@/fsd-pages/targets"));
const TrackingLinksPage = lazy(() => import("@/fsd-pages/tracking-links"));

export const router = createBrowserRouter([
    {
        element: <App />,
        errorElement: <GlobalErrorPage />,
        children: [
            {
                index: true,
                element: <RootRedirector />,
            },
            {
                element: <AuthLayout />,
                children: [
                    {
                        path: "login",
                        element: <LoginPage />,
                    },
                    {
                        path: "registration",
                        element: <RegistrationPage />,
                    },
                    {
                        path: "forgot-password",
                        element: <ForgotPasswordPage />,
                    },
                    {
                        path: "reset-password",
                        element: <ResetPasswordPage />,
                    },
                ],
            },
        ],
    },

    {
        element: (
            <SessionProvider>
                <AuthenticatedLayout />
            </SessionProvider>
        ),
        errorElement: <GlobalErrorPage />,
        children: [
            { path: ROUTES.CAMPAIGNS, element: <CampaignsPage /> },
            { path: "campaigns/create", element: <CreateCampaignPage key="create" /> },
            {
                path: "campaigns/edit/:uuid",
                element: (
                    <Suspense fallback={<Loader />}>
                        <CreateCampaignPage edit key="edit" />
                    </Suspense>
                ),
            },
            { path: "add-app", element: <AddAppPage /> },
            { path: "edit-app", element: <AddAppPage edit /> },
            { path: "assets", element: <AssetsPage /> },
            { path: "auditories", element: <AuditoriesPage /> },
            {
                path: "auditories/edit/:uuid",
                element: (
                    <Suspense fallback={<Loader />}>
                        <AuditoriesPage edit />
                    </Suspense>
                ),
            },
            { path: "creatives", element: <CreativesPage /> },
            { path: "creatives/add-creative", element: <AddCreativePage /> },
            { path: "creatives/edit", element: <AddCreativePage edit /> },
            { path: "finances", element: <FinancesPage /> },
            { path: "payment/success", element: <PaymentSuccessPage /> },
            { path: "payment/failed", element: <PaymentFailedPage /> },
            { path: "profile", element: <ProfilePage /> },
            { path: "reports", element: <ReportsPage /> },
            { path: "targets", element: <TargetsPage /> },
            {
                path: "targets/edit/:uuid",
                element: (
                    <Suspense fallback={<Loader />}>
                        {" "}
                        <TargetsPage edit />{" "}
                    </Suspense>
                ),
            },
            { path: "tracking-links", element: <TrackingLinksPage /> },
            {
                path: "tracking-links/edit/:uuid",
                element: (
                    <Suspense fallback={<Loader />}>
                        <TrackingLinksPage edit />
                    </Suspense>
                ),
            },
        ],
    },

    {
        path: "*",
        element: <NotFoundPage />,
    },
]);
